import { useState } from "react";
import { observer } from "mobx-react";
import { toast } from "sonner";
import { useTranslation } from "@/i18n";
import { isValidEmail } from "@/lib/email";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useStore } from "@/stores/store-context";
import { requestErrorSummary } from "@/lib/errors/request-error-summary";
import { emailChangeErrorHandler } from "@/helpers/authentication.helper";

const EMAIL_CODE_ATTEMPTS_EXCEEDED = "EMAIL_CODE_ATTEMPTS_EXCEEDED";
const EMAIL_CODE_EXPIRED = "EMAIL_CODE_EXPIRED";

const errorCode = (error: unknown): string | undefined => requestErrorSummary(error).code;

interface ChangeEmailModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ChangeEmailModal = observer(function ChangeEmailModal({
  open,
  onOpenChange,
}: ChangeEmailModalProps) {
  const { t } = useTranslation();
  const { user } = useStore();

  const [step, setStep] = useState<"email" | "code">("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);

  const reset = () => {
    setStep("email");
    setEmail("");
    setCode("");
    setIsSending(false);
    setIsVerifying(false);
  };

  const close = () => {
    onOpenChange(false);
    setTimeout(reset, 200);
  };

  const handleSend = async () => {
    const trimmed = email.trim().toLowerCase();
    if (!isValidEmail(trimmed)) {
      toast.error(t("profile_settings.email_change.invalid_email"));
      return;
    }
    setIsSending(true);
    try {
      await user.generateEmailChangeCode(trimmed);
      toast.success(t("profile_settings.email_change.code_sent"));
      setStep("code");
    } catch (error) {
      console.error("[profile] email change code request failed", requestErrorSummary(error));
      toast.error(emailChangeErrorHandler(errorCode(error), "send_error"));
    } finally {
      setIsSending(false);
    }
  };

  const handleVerify = async () => {
    if (!code.trim()) return;
    setIsVerifying(true);
    try {
      await user.updateEmail(email.trim().toLowerCase(), code.trim());
      toast.success(t("profile_settings.email_change.success"));
      close();
    } catch (error) {
      console.error("[profile] email change verify failed", requestErrorSummary(error));
      const code = errorCode(error);
      toast.error(emailChangeErrorHandler(code, "verify_error"));
      // A burnt or expired code can't be retried: back to the address step for a new one.
      if (code === EMAIL_CODE_ATTEMPTS_EXCEEDED || code === EMAIL_CODE_EXPIRED) {
        setCode("");
        setStep("email");
      }
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("profile_settings.email_change.title")}</DialogTitle>
          <DialogDescription>
            {step === "email"
              ? t("profile_settings.email_change.step1_description")
              : t("profile_settings.email_change.step2_description", { email })}
          </DialogDescription>
        </DialogHeader>

        {step === "email" ? (
          <div className="space-y-2">
            <Label htmlFor="email-change-new">
              {t("profile_settings.email_change.new_email_label")}
            </Label>
            <Input
              id="email-change-new"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              autoFocus
              disabled={isSending}
            />
          </div>
        ) : (
          <div className="space-y-2">
            <Label htmlFor="email-change-code">
              {t("profile_settings.email_change.code_label")}
            </Label>
            <Input
              id="email-change-code"
              type="text"
              inputMode="numeric"
              value={code}
              onChange={(event) => setCode(event.target.value)}
              autoFocus
              disabled={isVerifying}
            />
          </div>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={close}
            disabled={isSending || isVerifying}
          >
            {t("common.cancel")}
          </Button>
          {step === "email" ? (
            <Button
              type="button"
              onClick={() => void handleSend()}
              disabled={!email.trim() || isSending}
            >
              {isSending
                ? t("profile_settings.email_change.sending")
                : t("profile_settings.email_change.send_code")}
            </Button>
          ) : (
            <Button
              type="button"
              onClick={() => void handleVerify()}
              disabled={!code.trim() || isVerifying}
            >
              {isVerifying
                ? t("profile_settings.email_change.verifying")
                : t("profile_settings.email_change.verify")}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
});
